import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { prefersReducedMotion } from 'src/utils/motion';

import { usePage } from '../../pages';
import { N, SCENES, X0, X1 } from './scenes';

import style from './Problems.module.scss';

// Width of the soft edge where the line morphs, as a share of the slider width
const BAND = 0.05;
const KEY_STEPS = { ArrowLeft: -0.05, ArrowDown: -0.05, ArrowRight: 0.05, ArrowUp: 0.05, PageDown: -0.2, PageUp: 0.2 };

const clamp01 = (v) => Math.max(0, Math.min(1, v));
const lerp = (a, b, t) => a + (b - a) * t;
const smoothstep = (a, b, x) => {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
};
const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

const Stat = ({ chip, value, caption }) => (
  <div className={style.stat}>
    <span className={style.chip}>{chip}</span>
    <span className={style.num}>{value}</span>
    <span className={style.cap}>{caption}</span>
  </div>
);

// Process line: straightened (and glowing) left of the slider, tangled right of it
const Lines = ({ index, p, width: W, height: H, labels }) => {
  const scene = SCENES[index];
  const fs = Math.max(11, Math.min(14, W * 0.021));
  const tAt = (x) => 1 - smoothstep(p - BAND, p + BAND, x);
  const X = (x) => x * W;
  const Y = (y) => (0.4 + y * 0.48) * H;
  const ti = (i) => tAt(scene.B[i][0]);
  const at = (i) => {
    const a = scene.A[i];
    const b = scene.B[i];
    const t = ti(i);
    return [lerp(a[0], b[0], t), lerp(a[1], b[1], t)];
  };

  let d = '';
  let closest = 0;
  for (let i = 0; i < N; i++) {
    const q = at(i);
    d += `${i ? 'L' : 'M'}${X(q[0]).toFixed(1)},${Y(q[1]).toFixed(1)}`;
    if (Math.abs(scene.B[i][0] - p) < Math.abs(scene.B[closest][0] - p)) closest = i;
  }

  const comb = at(closest);
  const { under, over } = scene.decorate({ X, Y, tAt, ti, at, fs, edge: Math.min(X1, p - BAND) }, labels);
  const id = (name) => `ba-${name}-${index}`;

  return (
    <>
      <defs>
        <linearGradient
          id={id('line')}
          gradientUnits='userSpaceOnUse'
          x1='0'
          y1='0'
          x2={W}
          y2='0'
        >
          <stop
            offset='0'
            stopColor='#9BF0C9'
          />
          <stop
            offset={Math.max(0, p - BAND).toFixed(4)}
            stopColor='#9BF0C9'
          />
          <stop
            offset={Math.min(1, p + BAND).toFixed(4)}
            stopColor='#02231F'
          />
          <stop
            offset='1'
            stopColor='#02231F'
          />
        </linearGradient>
        <clipPath id={id('clip')}>
          <rect
            x='0'
            y='0'
            width={Math.max(0, p * W).toFixed(1)}
            height={H}
          />
        </clipPath>
        <filter
          id={id('blur')}
          filterUnits='userSpaceOnUse'
          x='0'
          y='0'
          width={W}
          height={H}
        >
          <feGaussianBlur stdDeviation='5' />
        </filter>
        <radialGradient id={id('comb')}>
          <stop
            offset='0'
            stopColor='#fff'
          />
          <stop
            offset='.3'
            stopColor='#9BF0C9'
            stopOpacity='.8'
          />
          <stop
            offset='1'
            stopColor='#9BF0C9'
            stopOpacity='0'
          />
        </radialGradient>
      </defs>
      <g>{under}</g>
      <g clipPath={`url(#${id('clip')})`}>
        <path
          className={style.glow}
          d={d}
          fill='none'
          stroke='#9BF0C9'
          strokeWidth='10'
          strokeOpacity='.22'
          strokeLinecap='round'
          strokeLinejoin='round'
          filter={`url(#${id('blur')})`}
        />
      </g>
      <path
        d={d}
        fill='none'
        stroke={`url(#${id('line')})`}
        strokeWidth='3'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <g>{over}</g>
      {p > X0 - 0.02 && p < X1 + 0.02 && (
        <circle
          className={style.comb}
          cx={X(comb[0]).toFixed(1)}
          cy={Y(comb[1]).toFixed(1)}
          r='24'
          fill={`url(#${id('comb')})`}
        />
      )}
    </>
  );
};

const BeforeAfter = ({ index, item, labels }) => {
  const { t } = useTranslation('page');
  const { id } = usePage();
  const ref = useRef(null);
  const value = useRef(prefersReducedMotion ? 0.5 : 0.04);
  const raf = useRef(0);
  const timers = useRef({});
  const demoOn = useRef(false);
  const touchedRef = useRef(false);
  const pointer = useRef({ drag: false, start: null });

  const [p, setP] = useState(value.current);
  const [size, setSize] = useState(null);
  const [moving, setMoving] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [touched, setTouched] = useState(false);
  const [ready, setReady] = useState(prefersReducedMotion);
  const [lit, setLit] = useState(false);

  // The glow under the slider shows only while it moves
  const show = (v) => {
    value.current = v;
    setP(v);
    setMoving(true);
    clearTimeout(timers.current.moving);
    timers.current.moving = setTimeout(() => setMoving(false), 500);
  };

  const tween = (to, duration, done) => {
    cancelAnimationFrame(raf.current);
    const from = value.current;
    const t0 = performance.now();
    const step = (now) => {
      const k = Math.min(1, (now - t0) / duration);
      show(lerp(from, to, easeInOutCubic(k)));
      if (k < 1) raf.current = requestAnimationFrame(step);
      else done?.();
    };
    raf.current = requestAnimationFrame(step);
  };

  const stopDemo = () => {
    demoOn.current = false;
    cancelAnimationFrame(raf.current);
    clearTimeout(timers.current.demo);
  };

  // First view: sweep across the scene, flash the line, settle in the middle
  const runDemo = () => {
    if (prefersReducedMotion || touchedRef.current) return;
    demoOn.current = true;
    tween(0.96, 2400, () => {
      if (!demoOn.current) return;
      setLit(true);
      timers.current.demo = setTimeout(() => {
        if (!demoOn.current) return;
        tween(0.5, 1000, () => {
          demoOn.current = false;
          setReady(true);
        });
      }, 750);
    });
  };

  const touch = () => {
    stopDemo();
    touchedRef.current = true;
    setTouched(true);
    setReady(true);
  };

  useEffect(() => {
    const el = ref.current;
    const resizeObserver = new ResizeObserver(() => {
      const { width, height } = el.getBoundingClientRect();
      setSize(width && height ? { width, height } : null);
    });
    const visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        visibilityObserver.disconnect();
        timers.current.demo = setTimeout(runDemo, 250);
      },
      { threshold: 0.55 }
    );
    resizeObserver.observe(el);
    visibilityObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
      visibilityObserver.disconnect();
      cancelAnimationFrame(raf.current);
      Object.values(timers.current).forEach(clearTimeout);
    };
  }, []);

  const positionOf = (e) => {
    const rect = ref.current.getBoundingClientRect();
    return clamp01((e.clientX - rect.left) / rect.width);
  };

  const beginDrag = (e) => {
    touch();
    pointer.current.drag = true;
    setDragging(true);
    ref.current.setPointerCapture?.(e.pointerId);
  };

  const endDrag = () => {
    pointer.current = { drag: false, start: null };
    setDragging(false);
  };

  // Mouse drags right away, touch only after a horizontal move (vertical scrolls the page)
  const handlePointerDown = (e) => {
    if (e.button > 0) return;
    if (e.pointerType === 'mouse') {
      e.preventDefault();
      beginDrag(e);
      show(positionOf(e));
    } else {
      pointer.current.start = { x: e.clientX, y: e.clientY };
    }
  };

  const handlePointerMove = (e) => {
    const { drag, start } = pointer.current;
    if (!drag && start) {
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      if (Math.abs(dx) > 6 && Math.abs(dx) > Math.abs(dy)) beginDrag(e);
      else if (Math.abs(dy) > 10) pointer.current.start = null;
    }
    if (pointer.current.drag) show(positionOf(e));
  };

  const handlePointerUp = (e) => {
    const { drag, start } = pointer.current;
    if (!drag && start) {
      touch();
      tween(positionOf(e), 450);
    }
    endDrag();
  };

  const handleKeyDown = (e) => {
    const step = KEY_STEPS[e.key];
    if (step === undefined && e.key !== 'Home' && e.key !== 'End') return;
    e.preventDefault();
    touch();
    show(clamp01(e.key === 'Home' ? 0 : e.key === 'End' ? 1 : value.current + step));
  };

  const percent = Math.round(p * 100);

  return (
    <div
      ref={ref}
      className={cn(
        style.ba,
        moving && style.moving,
        dragging && style.drag,
        touched && style.touched,
        ready && style.ready,
        lit && style.lit
      )}
      style={{ '--p': p.toFixed(4) }}
      role='group'
      aria-label={item.aria}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={endDrag}
    >
      <div className={cn(style.pane, style.after)}>
        <Stat
          chip={labels.with}
          {...item.after}
        />
      </div>
      <div className={cn(style.pane, style.before)}>
        <Stat
          chip={labels.without}
          {...item.before}
        />
      </div>
      <svg
        aria-hidden='true'
        viewBox={size ? `0 0 ${size.width.toFixed(1)} ${size.height.toFixed(1)}` : undefined}
      >
        {size && (
          <Lines
            index={index}
            p={p}
            width={size.width}
            height={size.height}
            labels={labels.chart}
          />
        )}
      </svg>
      <div className={style.handle}>
        <button
          type='button'
          className={style.knob}
          role='slider'
          aria-label={labels.slider}
          aria-valuemin='0'
          aria-valuemax='100'
          aria-valuenow={percent}
          aria-valuetext={t(`${id}.problems.sliderValue`, { value: percent })}
          onKeyDown={handleKeyDown}
        >
          <svg
            viewBox='0 0 26 16'
            fill='none'
            stroke='currentColor'
            strokeWidth='1.8'
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden='true'
          >
            <path d='M8 3L3 8l5 5M18 3l5 5-5 5' />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default BeforeAfter;
